import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogContentText,
  Stack,
  Typography,
} from '@mui/material';
import { RootDialog } from 'components/RootDialog';
import { FC, useState } from 'react';

interface ConfirmationDilogPropsModel {
  isLoading?: boolean;
  isOpen: boolean;
  title: string;
  content: string;
  onProceed: (callback?: VoidFunction) => void;
  onCancel: (callback?: VoidFunction) => void;
}

export const ConfirmationDialog: FC<ConfirmationDilogPropsModel> = ({
  isOpen,
  title,
  content,
  onProceed,
  onCancel,
  isLoading,
}) => {
  return (
    <>
      {isOpen && (
        <RootDialog
          maxWidth={'sm'}
          onClose={() => {
            onCancel();
          }}
          open={isOpen}
        >
          <DialogContent sx={{ pl: 0, pr: 0, width: 420, pt: 4, pb: 2 }}>
            <Stack alignItems="center" spacing={2}>
              <WarningAmberRoundedIcon color="warning" sx={{ fontSize: 48 }} />
              {title && (
                <Typography
                  variant="inter20"
                  fontWeight={'fontWeightSemibold'}
                  color="common.black"
                  sx={{ lineHeight: 'normal', textAlign: 'center' }}
                >
                  {title}
                </Typography>
              )}
              <DialogContentText
                sx={{ textAlign: 'center', color: 'colors.gray.o70', fontSize: 16 }}
              >
                {content}
              </DialogContentText>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button
              variant="text"
              color="info"
              size="large"
              sx={{ mr: 2, minWidth: 120 }}
              onClick={() => onCancel()}
              disabled={isLoading}
            >
              <Typography variant="inter16" fontWeight={600}>
                Cancel
              </Typography>
            </Button>

            <Button
              variant="contained"
              color="error"
              size="large"
              sx={{ minWidth: 120 }}
              onClick={() => {
                onProceed();
              }}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              <Typography variant="inter16" fontWeight={600}>
                Confirm
              </Typography>
            </Button>
          </DialogActions>
        </RootDialog>
      )}
    </>
  );
};

/**
 *
 */

export function withConfirmationDilog<P extends object>(Component: React.ComponentType<P>) {
  const ConfirmationAction = ({
    isLoading = false,
    title = '',
    content = '',
    onProceed = () => {},
    onCancel = () => {},
    ...props
  }: Omit<ConfirmationDilogPropsModel, 'isOpen'> & P) => {
    const [open, setOpen] = useState<boolean>(false);

    return (
      <>
        <Box
          onClick={e => {
            e.stopPropagation();
            setOpen(true);
          }}
        >
          <Component {...(props as P)} />
        </Box>
        <ConfirmationDialog
          isLoading={isLoading}
          isOpen={open}
          title={title}
          content={content}
          onProceed={() => {
            setOpen(false);
            onProceed();
          }}
          onCancel={() => {
            setOpen(false);
            onCancel();
          }}
        />
      </>
    );
  };

  ConfirmationAction.displayName = `WithConfirmationDilog`;

  return ConfirmationAction;
}
